/* eslint-disable prettier/prettier */
// hooks/useUsers.ts

import { useState, useCallback } from 'react';
import { userService } from '../services/allUserService';
import { User } from '../types/user';

export const useUsers = () => {
	const [users, setUsers] = useState<User[]>([]);
	const [coaches, setCoaches] = useState<User[]>([]);
	const [admins, setAdmins] = useState<User[]>([]);

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// 🔥 Generic users fetch
	const fetchUsers = useCallback(
		async (filters?: {
			role?: string;
			accountstatus?: string;
			search?: string;
			isactive?: boolean;
			page?: number;
			limit?: number;
		}) => {
			try {
				setLoading(true);
				setError(null);

				const res = await userService.getUsers(filters);

				if (res.success) {
					setUsers(res.data?.users || []);
				} else {
					setError(res.message || 'Failed to fetch users');
				}
			} catch (err: any) {
				console.error('fetchUsers error:', err);
				setError(err?.message || 'Something went wrong');
			} finally {
				setLoading(false);
			}
		},
		[]
	);

	// 🔥 Coaches / Mentors
	const fetchCoaches = useCallback(async (type: 'coach' | 'mentor') => {
		try {
			setLoading(true);
			setError(null);

			const res = await userService.getCoaches(type);

			if (res.success) {
				setCoaches(res.data || []);
			} else {
				setError(res.message || 'Failed to fetch coaches');
			}
		} catch (err: any) {
			console.error('fetchCoaches error:', err);
			setError(err?.message || 'Something went wrong');
		} finally {
			setLoading(false);
		}
	}, []);

	// 🔥 Admin only
	const fetchAdmins = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);

			const res = await userService.getAdmins();

			if (res.success) {
				setAdmins(res.data?.users || []);
			} else {
				setError(res.message || 'Failed to fetch admins');
			}
		} catch (err: any) {
			console.error('fetchAdmins error:', err);
			setError(err?.message || 'Something went wrong');
		} finally {
			setLoading(false);
		}
	}, []);

	// 🔥 Admin + Superadmin combined
	const fetchAdminAndSuperAdmins = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);

			const res = await userService.getAdminAndSuperAdmins();

			if (res.success) {
				setAdmins(res.data || []);
			} else {
				setError(res.message || 'Failed to fetch admin users');
			}
		} catch (err: any) {
			console.error('fetchAdminAndSuperAdmins error:', err);
			setError(err?.message || 'Something went wrong');
		} finally {
			setLoading(false);
		}
	}, []);

	return {
		users,
		coaches,
		admins,
		loading,
		error,

		fetchUsers,
		fetchCoaches,
		fetchAdmins,
		fetchAdminAndSuperAdmins,
	};
};
